import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useLanguage } from "@/components/sybrax/LanguageProvider";
import { content } from "@/components/sybrax/i18n";

const handleScroll = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
    }),
};

const Hero = () => {
    const { lang } = useLanguage();
    const hero = content[lang].hero;

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative min-h-screen flex items-center pt-28 sm:pt-32 pb-20 overflow-hidden"
        >
            <div className="absolute inset-0 bg-grid mask-radial opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-white dark:via-[#030712]/40 dark:to-[#030712]" />

            <div className="pointer-events-none absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full orb-primary blur-[120px] animate-pulse-glow" />
            <div className="pointer-events-none absolute top-1/3 -right-32 w-[460px] h-[460px] rounded-full orb-secondary blur-[120px] animate-pulse-glow" />
            <div className="noise-overlay" />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                    <div className="lg:col-span-7">
                        <motion.div
                            initial="hidden"
                            animate="show"
                            variants={fadeUp}
                            custom={0}
                            data-testid="hero-eyebrow"
                            className="inline-flex items-center gap-2 rounded-full border border-slate-300/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] backdrop-blur-md px-4 py-1.5 text-xs font-medium tracking-wider uppercase text-cyan-700 dark:text-cyan-300"
                        >
                            <Sparkles size={14} className="text-cyan-500 dark:text-cyan-400" />
                            {hero.eyebrow}
                        </motion.div>

                        <motion.h1
                            initial="hidden"
                            animate="show"
                            variants={fadeUp}
                            custom={1}
                            data-testid="hero-title"
                            className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 dark:text-white"
                        >
                            {hero.title}
                        </motion.h1>

                        <motion.p
                            initial="hidden"
                            animate="show"
                            variants={fadeUp}
                            custom={2}
                            data-testid="hero-tagline"
                            className="mt-4 font-display text-xl sm:text-2xl lg:text-3xl font-light text-slate-700 dark:text-slate-200"
                        >
                            {hero.tagline}
                        </motion.p>

                        <motion.p
                            initial="hidden"
                            animate="show"
                            variants={fadeUp}
                            custom={3}
                            data-testid="hero-description"
                            className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-600 dark:text-slate-400"
                        >
                            {hero.description}
                        </motion.p>

                        <motion.div
                            initial="hidden"
                            animate="show"
                            variants={fadeUp}
                            custom={4}
                            className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
                        >
                            <a
                                href="#products"
                                data-testid="hero-cta-products"
                                onClick={(e) => handleScroll(e, "#products")}
                                className="group inline-flex items-center justify-center gap-2 rounded-full bg-cyan-500 hover:bg-cyan-400 text-[#030712] text-sm font-semibold px-7 py-3.5 transition-all hover:shadow-[0_0_30px_rgba(6,182,212,0.55)]"
                            >
                                {hero.ctaPrimary}
                                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                            </a>
                            <a
                                href="#contact"
                                data-testid="hero-cta-contact"
                                onClick={(e) => handleScroll(e, "#contact")}
                                className="inline-flex items-center justify-center rounded-full border border-slate-300/80 dark:border-white/15 bg-white/60 dark:bg-white/[0.03] hover:bg-white dark:hover:bg-white/[0.07] hover:border-slate-400 dark:hover:border-white/25 text-slate-900 dark:text-white text-sm font-medium px-7 py-3.5 transition-all"
                            >
                                {hero.ctaSecondary}
                            </a>
                        </motion.div>

                        <motion.p
                            initial="hidden"
                            animate="show"
                            variants={fadeUp}
                            custom={5}
                            data-testid="hero-emphasis"
                            className="mt-8 text-sm text-slate-500 dark:text-slate-500"
                        >
                            <span className="inline-block w-8 h-px bg-cyan-500/70 dark:bg-cyan-400/60 align-middle mr-3" />
                            {hero.emphasis}
                        </motion.p>
                    </div>

                    <div className="lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.92 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
                            className="relative aspect-square w-full max-w-[520px] mx-auto"
                            data-testid="hero-visual"
                        >
                            <div className="absolute inset-[15%] rounded-full bg-gradient-to-br from-cyan-400/40 via-blue-500/30 to-purple-600/20 blur-2xl animate-pulse-glow" />
                            <div className="absolute inset-[28%] rounded-full bg-gradient-to-br from-cyan-300 to-blue-500 blur-xl opacity-50" />

                            <div className="absolute inset-0 rounded-full border border-slate-300/30 dark:border-white/5" />
                            <div className="absolute inset-[10%] rounded-full border border-cyan-500/10" />
                            <div className="absolute inset-[22%] rounded-full border border-cyan-500/20" />
                            <div className="absolute inset-[36%] rounded-full border border-cyan-400/30" />

                            <div className="absolute top-[8%] left-[6%] animate-float-slow">
                                <div className="glass-card !rounded-xl px-3.5 py-2.5 flex items-center gap-2 shadow-2xl">
                                    <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                                    <div className="text-[11px] font-medium text-slate-900 dark:text-slate-200">Mooddy</div>
                                    <div className="text-[10px] text-cyan-700 dark:text-cyan-300/80">MVP</div>
                                </div>
                            </div>

                            <div className="absolute top-[22%] right-[4%] animate-float-slower">
                                <div className="glass-card !rounded-xl px-3.5 py-2.5 flex items-center gap-2 shadow-2xl">
                                    <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                                    <div className="text-[11px] font-medium text-slate-900 dark:text-slate-200">uSeen</div>
                                    <div className="text-[10px] text-cyan-700 dark:text-cyan-300/80">MVP</div>
                                </div>
                            </div>

                            <div className="absolute top-[2%] right-[8%] animate-float-slower">
                                <div className="glass-card !rounded-xl px-3.5 py-2.5 flex items-center gap-2 shadow-2xl">
                                    <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                                    <div className="text-[11px] font-medium text-slate-900 dark:text-slate-200">Work Analyse</div>
                                    <div className="text-[10px] text-emerald-600 dark:text-emerald-300/80">READY</div>
                                </div>
                            </div>

                            <div className="absolute bottom-[20%] left-[2%] animate-float-slower">
                                <div className="glass-card !rounded-xl px-3.5 py-2.5 flex items-center gap-2 shadow-2xl">
                                    <div className="w-2 h-2 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.8)]" />
                                    <div className="text-[11px] font-medium text-slate-900 dark:text-slate-200">CodUpp</div>
                                    <div className="text-[10px] text-amber-600 dark:text-amber-300/80">DEV</div>
                                </div>
                            </div>

                            <div className="absolute bottom-[0%] left-[14%] animate-float-slow">
                                <div className="glass-card !rounded-xl px-3.5 py-2.5 flex items-center gap-2 shadow-2xl">
                                    <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                                    <div className="text-[11px] font-medium text-slate-900 dark:text-slate-200">Abonelikiptal</div>
                                    <div className="text-[10px] text-emerald-600 dark:text-emerald-300/80">READY</div>
                                </div>
                            </div>

                            <div className="absolute bottom-[6%] right-[10%] animate-float-slow">
                                <div className="glass-card !rounded-xl px-3.5 py-2.5 flex items-center gap-2 shadow-2xl">
                                    <div className="w-2 h-2 rounded-full bg-violet-400 shadow-[0_0_8px_rgba(167,139,250,0.8)]" />
                                    <div className="text-[11px] font-medium text-slate-900 dark:text-slate-200">Promlock</div>
                                    <div className="text-[10px] text-violet-600 dark:text-violet-300/80">SOON</div>
                                </div>
                            </div>

                            <div className="absolute inset-0 flex items-center justify-center">
                                <img
                                    src="/icon-512.png"
                                    alt=""
                                    aria-hidden="true"
                                    className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl shadow-[0_0_40px_rgba(6,182,212,0.35)] dark:shadow-[0_0_50px_rgba(6,182,212,0.5)] select-none"
                                    draggable="false"
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
